import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import { Formik } from "formik";
import TextFieldWrapper from "../FormComponents/TextFieldWrapper";
import { Cancel, Forward, Delete } from "@mui/icons-material";
import * as Yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import UserQuery from "../../stateQueries/User";
import AlertPopup from "../AlertPopup";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2)
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1)
  }
}));

export default function AddEditGalleryModal({ galleryItem }) {
  const [open, setOpen] = React.useState(false);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [previewURLs, setPreviewURLs] = React.useState([]);
  const fileInputRef = React.useRef(null);

  const queryClient = useQueryClient();

  const addGalleryMutation = useMutation({
    mutationFn: async (formData) => {
      return UserQuery.CSEQuery.addGalleryImage(formData);
    },
    onSuccess: (data) => {
      setTimeout(() => {
        queryClient.invalidateQueries(["gallery"]);
        setOpen(false);
      }, 2000);
    }
  });

  const editGalleryMutation = useMutation({
    mutationFn: async (formData) => {
      return UserQuery.CSEQuery.editGalleryImage(formData);
    },
    onSuccess: (data) => {
      setTimeout(() => {
        queryClient.invalidateQueries(["gallery"]);
        setOpen(false);
      }, 2000);
    }
  });

  const handleToggleOpen = () => {
    setOpen(!open);
    if (open) {
      setSelectedFiles([]);
      setPreviewURLs([]);
    }
  };

  const handleFileChange = (event, setFieldValue) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const maxFiles = 10;

    if (files.length + selectedFiles.length > maxFiles) {
      alert(`You can only upload up to ${maxFiles} images`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const newFiles = [...selectedFiles, ...files].slice(0, maxFiles);
    setSelectedFiles(newFiles);
    setFieldValue("files", newFiles);

    // Generate preview URLs for new files using Promise.all
    Promise.all(
      files.map((file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
      })
    ).then((newPreviews) => {
      setPreviewURLs((prev) => [...prev, ...newPreviews].slice(0, maxFiles));
    });

    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveImage = (index, setFieldValue) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previewURLs.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setPreviewURLs(newPreviews);
    setFieldValue("files", newFiles);
  };

  return (
    <div>
      {galleryItem ? (
        <IconButton onClick={handleToggleOpen} color="primary">
          <EditIcon />
        </IconButton>
      ) : (
        <Button variant="contained" onClick={handleToggleOpen}>
          Add Gallery Image
        </Button>
      )}

      {addGalleryMutation.isError && (
        <AlertPopup
          open={true}
          severity="error"
          message={
            addGalleryMutation.error?.response?.data?.message || "Server Error"
          }
        />
      )}
      {addGalleryMutation.isSuccess && (
        <AlertPopup open={true} message={addGalleryMutation.data?.message} />
      )}

      {editGalleryMutation.isError && (
        <AlertPopup
          open={true}
          severity="error"
          message={
            editGalleryMutation.error?.response?.data?.message || "Server Error"
          }
        />
      )}
      {editGalleryMutation.isSuccess && (
        <AlertPopup open={true} message={editGalleryMutation.data?.message} />
      )}

      <BootstrapDialog
        onClose={handleToggleOpen}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          {galleryItem ? "Edit Gallery Image" : "Add Gallery Image"}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleToggleOpen}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Formik
            initialValues={{
              title: galleryItem?.title || "",
              description: galleryItem?.description || "",
              files: []
            }}
            validationSchema={Yup.object().shape({
              title: Yup.string().required("Title is required"),
              description: Yup.string(),
              files: galleryItem
                ? Yup.array()
                : Yup.array()
                    .min(1, "At least one image is required")
                    .max(10, "Maximum 10 images allowed")
            })}
            onSubmit={(values, { setSubmitting }) => {
              const formData = new FormData();
              formData.append("title", values.title);
              formData.append("description", values.description);

              if (galleryItem) {
                formData.append("id", galleryItem.id);
              }

              // Append multiple files
              selectedFiles.forEach((file) => {
                formData.append("files", file);
              });

              if (galleryItem) {
                editGalleryMutation.mutate(formData);
              } else {
                addGalleryMutation.mutate(formData);
              }

              setSubmitting(false);
            }}
          >
            {({
              isSubmitting,
              setFieldValue,
              values,
              errors,
              touched,
              handleSubmit
            }) => (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  multiple
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, setFieldValue)}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextFieldWrapper name="title" label="Title" fullWidth />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldWrapper
                      name="description"
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={selectedFiles.length >= 10}
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Select Images (Max 10) - {selectedFiles.length}/10
                      selected
                    </Button>
                    {touched.files && errors.files && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 1 }}
                      >
                        {errors.files}
                      </Typography>
                    )}
                  </Grid>
                  {previewURLs.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected Images:
                      </Typography>
                      <Grid container spacing={1}>
                        {previewURLs.map((url, index) => (
                          <Grid item xs={6} sm={4} md={3} key={index}>
                            <Box
                              sx={{
                                position: "relative",
                                borderRadius: 1,
                                overflow: "hidden"
                              }}
                            >
                              <img
                                src={url}
                                alt={`Preview ${index + 1}`}
                                style={{
                                  width: "100%",
                                  height: 120,
                                  objectFit: "cover",
                                  borderRadius: 8
                                }}
                              />
                              <IconButton
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  backgroundColor: "rgba(255,255,255,0.8)",
                                  "&:hover": {
                                    backgroundColor: "rgba(255,255,255,1)"
                                  }
                                }}
                                onClick={() =>
                                  handleRemoveImage(index, setFieldValue)
                                }
                              >
                                <Delete fontSize="small" color="error" />
                              </IconButton>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}
                  {galleryItem &&
                    galleryItem.images &&
                    galleryItem.images.length > 0 &&
                    previewURLs.length === 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Current Images:
                        </Typography>
                        <Grid container spacing={1} justifyContent="center">
                          {galleryItem.images.map((image, index) => (
                            <Grid item xs={6} sm={4} md={3} key={index}>
                              <img
                                src={`${process.env.REACT_APP_API_URL}/uploads/gallery/${image}`}
                                alt={`${galleryItem.title} ${index + 1}`}
                                style={{
                                  width: "100%",
                                  height: 120,
                                  objectFit: "cover",
                                  borderRadius: 8
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    )}
                  <Grid item xs={12}>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="outlined"
                        onClick={handleToggleOpen}
                        startIcon={<Cancel />}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={
                          isSubmitting ||
                          addGalleryMutation.isLoading ||
                          editGalleryMutation.isLoading
                        }
                        startIcon={
                          addGalleryMutation.isLoading ||
                          editGalleryMutation.isLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Forward />
                          )
                        }
                      >
                        {galleryItem ? "Update" : "Submit"}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </div>
            )}
          </Formik>
        </DialogContent>
      </BootstrapDialog>
    </div>
  );
}
